import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Mail, Send } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store in localStorage for mock functionality
    const contacts = JSON.parse(localStorage.getItem('contacts') || '[]');
    contacts.push({
      ...formData,
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('contacts', JSON.stringify(contacts));
    
    alert('Thank you for contacting us! We will get back to you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div>
      <SEO 
        title="Contact"
        description="Get in touch with Munoret for all your staffing and recruitment needs. Email us at info@munoret.com. We're here to help you find the perfect talent match."
        keywords="contact Munoret, recruitment contact, staffing inquiry, talent solutions contact, email Munoret, get in touch"
      />
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(180deg, var(--bg-light) 0%, var(--bg-primary) 100%)',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '24px' }}
          >
            Get In Touch
          </motion.h1>
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}
          >
            Ready to transform your workforce? Let's discuss your staffing needs and how we can help.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px'
          }}>
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="heading-large" style={{ marginBottom: '24px' }}>
                Contact Information
              </h2>
              <p style={{ 
                color: 'var(--text-secondary)',
                marginBottom: '32px',
                lineHeight: '1.6'
              }}>
                Have questions? We're here to help. Reach out through any of these channels.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  padding: '24px',
                  background: 'var(--bg-light)',
                  borderRadius: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Mail size={24} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>Email Us</div>
                    <a href="mailto:info@munoret.com" style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none'
                    }}>
                      info@munoret.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div style={{
                background: 'white',
                padding: '48px',
                borderRadius: '24px',
                border: '1px solid var(--border-medium)'
              }}>
                <h2 className="heading-large" style={{ marginBottom: '24px' }}>
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="form-input"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="form-input"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Company
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="form-input"
                        placeholder="Your Company"
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="form-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Subject *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="form-input"
                        placeholder="How can we help?"
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: 'var(--text-primary)'
                      }}>
                        Message *
                      </label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="form-input"
                        placeholder="Tell us about your staffing needs..."
                        rows="5"
                      />
                    </div>

                    <button type="submit" className="btn-primary" style={{ 
                      width: '100%',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      Send Message
                      <Send size={20} />
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
